/**
 * ParagraphToPageMapper - Utility for building paragraph-to-page mappings
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for creating mappings between paragraph IDs
 * and the page indices where they appear, which is useful for pagination and
 * incremental layout operations.
 */

import type { PageLayout } from '../../../core/types/Typesetter';

/**
 * Build a map from paragraph IDs to page indices
 * 
 * Creates a mapping where each paragraph ID maps to an array of page indices
 * where that paragraph appears. This is useful for determining which pages
 * contain specific paragraphs during incremental layout.
 * 
 * NOTE: With the new "WASM as single source of truth" architecture where pages
 * contain resolved `lines` instead of `paragraphs` (slice references), this
 * mapping is less useful. The function handles both formats gracefully.
 * 
 * @param pages - Array of PageLayout objects from pagination result
 * @returns Map from paragraph_id to array of page indices
 */
export function buildParagraphToPageMap(pages: PageLayout[]): Map<string, number[]> {
  const paragraphToPageMap = new Map<string, number[]>();
  
  for (const page of pages) {
    // Handle new format: pages have `lines` directly (no paragraphs slice references)
    // In this case, we can't build a paragraph-to-page map because the paragraph
    // IDs aren't available. This is fine - the new architecture doesn't need this mapping.
    const paragraphSlices = (page as any).paragraphs;
    if (!paragraphSlices || !Array.isArray(paragraphSlices)) {
      // New format with resolved lines - skip this page
      continue;
    }
    
    for (const slice of paragraphSlices) {
      if (!paragraphToPageMap.has(slice.paragraph_id)) {
        paragraphToPageMap.set(slice.paragraph_id, []);
      }
      const pageIndices = paragraphToPageMap.get(slice.paragraph_id)!;
      if (!pageIndices.includes(page.page_index)) {
        pageIndices.push(page.page_index);
      }
    }
  }
  
  return paragraphToPageMap;
}

/**
 * Get page indices for a specific paragraph
 * 
 * @param paragraphToPageMap - Map from paragraph_id to page indices
 * @param paragraphId - Paragraph ID to look up
 * @returns Array of page indices where the paragraph appears, or empty array if not found
 */
export function getPageIndicesForParagraph(
  paragraphToPageMap: Map<string, number[]>,
  paragraphId: string
): number[] {
  return paragraphToPageMap.get(paragraphId) || [];
}

/**
 * Check if a paragraph appears on a specific page
 * 
 * @param paragraphToPageMap - Map from paragraph_id to page indices
 * @param paragraphId - Paragraph ID to check
 * @param pageIndex - Page index to check
 * @returns True if the paragraph appears on the specified page
 */
export function isParagraphOnPage(
  paragraphToPageMap: Map<string, number[]>,
  paragraphId: string,
  pageIndex: number
): boolean {
  const pageIndices = paragraphToPageMap.get(paragraphId);
  return pageIndices ? pageIndices.includes(pageIndex) : false;
}


